/* lp-kaching-bundle — upgrade the cart's Kaching bundle to a higher deal bar.

   WHY THIS EXISTS. Kaching prices bundles ENTIRELY from a line-item property,
   never from what the cart happens to contain. Every line it adds carries
   `__kaching_bundles` naming the deal, the deal bar (the tier) and a shared
   per-add instance id; a Shopify discount Function then reads those stamps
   and applies the tier's percentage to the main lines and 100% to the gifts.

   So the drawer's "make it a pair" card could not work by adding a hoodie.
   A bare /cart/add.js line is invisible to that Function, and the hoodie
   already in the cart stays stamped with the ONE-hoodie bar (0% off), so the
   cart never becomes a pair: the shopper tapped "Activate discount · £52.50"
   and got two hoodies at £75 each and no tangle. Reproduced at £150.00
   against the live store on 2026-08-20.

   Upgrading therefore means REBUILDING the bundle: drop the lines of the old
   instance and re-add every unit and every gift of the target bar under one
   fresh instance id. That is exactly what Kaching's own product-page add
   does, and a forged rebuild is byte-comparable to it.

   WHAT THE FUNCTION ACTUALLY VALIDATES, measured rather than assumed (each
   row is a cart built against the live store and priced by Kaching):

     deal + bid + main/gift + shared instance id .. REQUIRED
     v   (deal version uuid) ...................... ignored — wrong or absent still prices
     pp  (parent product id) ...................... ignored — absent still prices

   We send `v` and `pp` anyway whenever the cart can tell us them, so our
   payload stays a faithful copy of Kaching's rather than a minimal one that
   happens to work today. Nothing here DEPENDS on them, which is why a cart
   with no stamped line left can still be upgraded.

   THE CONFIG IS READ LIVE, NEVER HARDCODED. Deal ids, bar ids and gift ids
   all change when the merchant edits the offer in the Kaching admin, and a
   stale gift id means a £299 community membership billed as a free gift. So
   the offer comes from window.kachingBundlesApi.fetchDeals() — present on
   every page, not just the product page, and it applies the shopper's own
   A/B allocation, which parsing the product page's inline settings would not.

   THE RULES, inherited from assets/lp-cart-ladder.js because this file is the
   other one that writes to a live shopper's cart:

     1. ONLY LINES WE CAN ACCOUNT FOR. A line is in scope only if it is a
        qualifying unit of the bundle product or carries a `__kaching_bundles`
        stamp for an instance holding one. Anything else is untouchable.
     2. ONLY BY LINE KEY. Never by variant id — two lines can share a variant.
     3. NOTHING IS SILENTLY LOST. A qualifying line that gets re-added comes
        back with the same variant, the same quantity and all of its other
        properties. The shopper owns exactly what they owned before, plus the
        second unit they asked for.
     4. WHEN ANYTHING IS AMBIGUOUS, DO NOTHING. No target bar, an unresolvable
        gift variant, an unparseable stamp: plan() returns null and the cart is
        left alone. A wrong rebuild overcharges somebody or gives away a £299
        product; the cost of the miss is one un-taken upsell.
     5. VERIFY, THEN COMMIT. The write is followed by a re-read that asserts
        every gift really is £0 and every unit really is discounted. If it is
        not, the snapshot taken before the write is restored. This is the only
        defence against Kaching changing its contract under us, and it is why
        the remove happens BEFORE the add (see execute()).

   THE PLANNER IS PURE ON PURPOSE, exactly as in lp-cart-ladder.js: planUpgrade
   takes cart lines plus config and returns intent. scripts/kaching-bundle-plan.test.mjs
   interrogates it without a browser. If a change here makes those tests want a
   document, logic has leaked out of the pure layer — move it back. */
(() => {
  'use strict';

  const STAMP = '__kaching_bundles';
  const GIFT_PROP = '_cn_gift';

  /* ---------------------------------------------------------------- pure */

  const props = (line) => (line && line.properties) || {};

  const readId = (value) => {
    const n = Math.trunc(Number(value));
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  const readQty = (value) => {
    const n = Math.trunc(Number(value));
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  /* Kaching stores JSON inside a property VALUE, so the stamp is a string that
     has to be parsed. Rule 4: a stamp we cannot read makes the whole cart
     ambiguous rather than merely uninteresting, so callers treat null as
     "stop", never as "no stamp". Use hasStamp() to tell the two apart. */
  const readStamp = (line) => {
    const raw = props(line)[STAMP];
    if (typeof raw !== 'string') return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  };

  const hasStamp = (line) => typeof props(line)[STAMP] === 'string';

  const isGiftLine = (line) => Object.prototype.hasOwnProperty.call(props(line), GIFT_PROP);

  /* A qualifying unit: the bundle product, bought rather than gifted. Gift
     lines are excluded so a gifted hoodie could never inflate the tier that
     earned it, and stamped gift lines of OTHER products are excluded because
     they are not units at all. */
  const isQualifyingUnit = (line, productId) => {
    if (!line || isGiftLine(line)) return false;
    if (readId(line.product_id) !== productId) return false;
    const stamp = readStamp(line);
    return !(stamp && stamp.gift); // a stamped gift hoodie is a gift, not a unit
  };

  const countUnits = (lines, productId) => {
    if (!Array.isArray(lines) || !productId) return 0;
    return lines.reduce(
      (total, line) => (isQualifyingUnit(line, productId) ? total + (readQty(line.quantity) || 0) : total),
      0
    );
  };

  /* Every line that must come out: the qualifying units themselves (they are
     re-added stamped for the new bar) plus every line of every instance that
     holds one of them. Collecting whole instances rather than just the hoodie
     is what stops the old bar's gifts being left behind — they would keep
     their £0 allocation from a bundle that no longer exists. */
  const linesToReplace = (lines, productId) => {
    if (!Array.isArray(lines) || !productId) return null;

    const instanceIds = new Set();
    for (const line of lines) {
      if (!isQualifyingUnit(line, productId)) continue;
      if (!hasStamp(line)) continue; // an unstamped unit belongs to no instance
      const stamp = readStamp(line);
      if (!stamp) return null; // rule 4 — unreadable stamp on a unit
      if (stamp.id) instanceIds.add(stamp.id);
    }

    const units = [];
    const others = [];
    for (const line of lines) {
      if (isQualifyingUnit(line, productId)) {
        units.push(line);
        continue;
      }
      if (!hasStamp(line)) continue;
      const stamp = readStamp(line);
      if (!stamp) continue; // not a unit, so an unreadable stamp is simply not ours
      if (stamp.id && instanceIds.has(stamp.id)) others.push(line);
    }
    return { instanceIds, units, others };
  };

  /* The highest quantity-break bar the unit count reaches. The top bar is a
     "3+", so `units >= quantity` rather than an equality test. */
  const pickBar = (deal, units) => {
    const bars = deal && Array.isArray(deal.dealBars) ? deal.dealBars : [];
    let best = null;
    for (const bar of bars) {
      const q = readQty(bar && bar.quantity);
      if (!q || units < q) continue;
      if (!best || q > readQty(best.quantity)) best = bar;
    }
    return best;
  };

  /* Properties to carry over when a line is re-added. Everything except our
     own stamp survives (rule 3) — a personalisation or an app's own marker is
     the shopper's, not ours to drop. */
  const carriedProps = (line) => {
    const out = {};
    for (const [key, value] of Object.entries(props(line))) {
      if (key === STAMP) continue;
      out[key] = value;
    }
    return out;
  };

  const planUpgrade = ({
    lines,
    deal,
    bar,
    productId,
    addVariantId = null,
    instanceId,
    variantForProduct = () => null,
    version = null,
  } = {}) => {
    const pid = readId(productId);
    if (!Array.isArray(lines) || !deal || !bar || !pid || !instanceId) return null;
    if (!deal.nanoId || !bar.id) return null; // rule 4 — cannot name the deal or the tier

    const replace = linesToReplace(lines, pid);
    if (!replace) return null;

    /* Units, merged per variant. Merging matters: Kaching itself posts one
       line of quantity 2 for a same-variant pair, and Shopify would merge two
       identical adds anyway, so planning per-variant keeps the diff describing
       a cart shape that can actually exist. */
    const byVariant = new Map();
    const propsFor = new Map();
    for (const line of replace.units) {
      const variantId = readId(line.variant_id);
      const qty = readQty(line.quantity);
      if (!variantId || !qty) return null; // rule 4
      byVariant.set(variantId, (byVariant.get(variantId) || 0) + qty);
      if (!propsFor.has(variantId)) propsFor.set(variantId, carriedProps(line));
    }
    const extra = readId(addVariantId);
    if (extra) {
      byVariant.set(extra, (byVariant.get(extra) || 0) + 1);
      if (!propsFor.has(extra)) propsFor.set(extra, {});
    }
    if (byVariant.size === 0) return null;

    const mainStamp = { deal: deal.nanoId, pp: pid, main: true, id: instanceId, bid: bar.id };
    if (version) mainStamp.v = version;

    const adds = [];
    for (const [variantId, quantity] of byVariant) {
      adds.push({
        id: variantId,
        quantity,
        properties: {
          ...propsFor.get(variantId),
          [STAMP]: JSON.stringify(mainStamp),
        },
      });
    }

    /* Gifts. variantId is null in the deal config for a product gift, so the
       caller resolves it; an unresolvable one stands the whole plan down
       rather than shipping a bundle missing a gift the card promised. */
    const giftVariantIds = [];
    for (const gift of Array.isArray(bar.freeGifts) ? bar.freeGifts : []) {
      if (!gift || !gift.id) return null;
      const variantId =
        readId(gift.variantId) ||
        readId(Array.isArray(gift.variantIds) ? gift.variantIds[0] : null) ||
        readId(variantForProduct(readId(gift.productId)));
      if (!variantId) return null; // rule 4
      const quantity = readQty(gift.quantity) || 1;
      const giftStamp = { id: instanceId, deal: deal.nanoId, gift: gift.id };
      if (version) giftStamp.v = version;
      adds.push({ id: variantId, quantity, properties: { [STAMP]: JSON.stringify(giftStamp) } });
      giftVariantIds.push(variantId);
    }

    /* Rule 2 — by line key. */
    const removes = [...replace.units, ...replace.others]
      .map((line) => line.key)
      .filter((key) => typeof key === 'string' && key);

    return {
      removes,
      adds,
      /* What execute() checks the resulting cart against (rule 5). */
      expect: {
        barId: bar.id,
        barTitle: bar.title || '',
        giftVariantIds,
        mainVariantIds: [...byVariant.keys()],
        instanceId,
      },
    };
  };

  window.lpKachingBundle = {
    STAMP,
    readStamp,
    hasStamp,
    countUnits,
    linesToReplace,
    pickBar,
    planUpgrade,
  };

  /* ------------------------------------------------------------- runtime
     Everything below touches the network. Nothing below is imported by the
     tests — the guard is window.location because only a browser has one, and
     the vm stub in the tests must provide a document for the IIFE to evaluate
     at all, so testing for a document would prove nothing. */
  if (typeof window === 'undefined' || !window.location) return;

  const routes = () => (window.theme && window.theme.routes) || {};
  const url = (name, fallback) => `${routes()[name] || fallback}.js`.replace('.js.js', '.js');

  const postJSON = (endpoint, body) =>
    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw Object.assign(new Error(data.description || data.message || 'Cart error'), { data });
      }
      return data;
    });

  const getCart = () =>
    fetch(`${(routes().root_url || '/').replace(/\/$/, '')}/cart.js`, {
      headers: { Accept: 'application/json' },
    }).then((r) => r.json());

  /* Gift variants: the deal config names a product, not a variant. Kaching
     itself picks the product's first available variant — verified against four
     live gifts — so that is what we reproduce. One small request, cached for
     the page, and it only covers PUBLISHED products, which every gift must be
     to be addable at all. */
  let productIndex = null;
  const loadProductIndex = async () => {
    if (productIndex) return productIndex;
    const root = (routes().root_url || '/').replace(/\/$/, '');
    const data = await fetch(`${root}/products.json?limit=250`, {
      headers: { Accept: 'application/json' },
    }).then((r) => r.json());
    productIndex = new Map();
    for (const product of (data && data.products) || []) {
      const available = (product.variants || []).find((v) => v.available) || (product.variants || [])[0];
      if (available) productIndex.set(Number(product.id), Number(available.id));
    }
    return productIndex;
  };

  /* Kaching's own instance ids are four characters of a nanoid alphabet
     ("iN9J", "4gTG", "t0Pf"). Ours look the same so a cart is indistinguishable
     from one the product page built. */
  const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const newInstanceId = () => {
    let out = '';
    const bytes = new Uint8Array(4);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    for (const b of bytes) out += ALPHABET[b % ALPHABET.length];
    return out;
  };

  const fetchDeal = async (stampedDealId) => {
    const api = window.kachingBundlesApi;
    if (!api || typeof api.fetchDeals !== 'function') return null;
    const deals = await api.fetchDeals();
    if (!Array.isArray(deals) || deals.length === 0) return null;
    /* Prefer the deal the cart is already stamped with — with several deals
       live, that is the one this shopper was actually shown. */
    if (stampedDealId) {
      const match = deals.find((d) => d && d.nanoId === stampedDealId);
      if (match) return match;
    }
    return deals[0];
  };

  /* Rule 5. Gifts must be free and units must be discounted; anything else
     means the contract moved and the cart has to go back. */
  const verify = (cart, expect) => {
    const items = (cart && cart.items) || [];
    const ours = items.filter((line) => {
      const stamp = readStamp(line);
      return stamp && stamp.id === expect.instanceId;
    });
    const gifts = ours.filter((line) => {
      const stamp = readStamp(line);
      return stamp && stamp.gift;
    });
    if (gifts.length !== expect.giftVariantIds.length) return 'a gift line is missing';
    if (gifts.some((line) => line.final_line_price !== 0)) return 'a gift line is not free';

    const units = ours.filter((line) => {
      const stamp = readStamp(line);
      return stamp && stamp.main;
    });
    if (units.length === 0) return 'no discounted unit line';
    /* The single-unit bar is a real, legitimate 0% tier, so only demand a
       discount when the bar we asked for actually discounts. */
    const wantsDiscount = expect.giftVariantIds.length > 0 || expect.mainVariantIds.length > 0;
    if (wantsDiscount && units.some((line) => line.final_line_price >= line.original_line_price)) {
      return 'a unit line was not discounted';
    }
    return null;
  };

  /* The snapshot is what makes this safe: enough to rebuild every line we are
     about to delete, including its properties. */
  const snapshot = (lines) =>
    lines.map((line) => ({
      id: Number(line.variant_id),
      quantity: Number(line.quantity),
      properties: { ...(line.properties || {}) },
    }));

  const restore = async (saved, addedKeys) => {
    /* Remove only what we created, by key (rule 2), then put back what we
       took. Both halves are best-effort: a cart that ends up with one line too
       many is recoverable by the shopper, a cart missing their hoodie is not,
       so the re-add goes first. */
    if (saved.length) await postJSON(url('cart_add_url', '/cart/add'), { items: saved });
    if (addedKeys.length) {
      const updates = {};
      for (const key of addedKeys) updates[key] = 0;
      await postJSON(url('cart_update_url', '/cart/update'), { updates });
    }
  };

  /* Upgrade the cart to the bar that `extraUnits` more units of `productId`
     would reach. Resolves to { ok, reason } and NEVER throws at the caller —
     the pair card turns a falsy result into its inline error. */
  const upgrade = async ({ productId, addVariantId = null, extraUnits = 1 } = {}) => {
    try {
      const pid = Number(productId);
      const before = await getCart();
      const lines = before.items || [];

      const stampedUnit = lines.find((line) => countUnits([line], pid) > 0 && hasStamp(line));
      const stamp = stampedUnit ? readStamp(stampedUnit) : null;

      const deal = await fetchDeal(stamp && stamp.deal);
      if (!deal) return { ok: false, reason: 'kaching-unavailable' };

      const units = countUnits(lines, pid) + Math.max(1, Number(extraUnits) || 1);
      const bar = pickBar(deal, units);
      if (!bar) return { ok: false, reason: 'no-bar' };

      const index = await loadProductIndex();
      const plan = planUpgrade({
        lines,
        deal,
        bar,
        productId: pid,
        addVariantId,
        instanceId: newInstanceId(),
        variantForProduct: (gid) => index.get(gid) || null,
        version: (stamp && stamp.v) || null,
      });
      if (!plan) return { ok: false, reason: 'ambiguous' };

      const replaced = linesToReplace(lines, pid);
      const saved = snapshot([...replaced.units, ...replaced.others]);

      /* REMOVE BEFORE ADD, which inverts lp-cart-ladder.js's rule 5 on
         purpose. That rule protects a THRESHOLD from dipping mid-operation and
         re-triggering itself; nothing here is threshold-driven. What matters
         instead is that the cart never holds two bundles at once — with the old
         1-hoodie instance still present the cart has three hoodies, Kaching
         prices it as the 3+ tier, and the verification below could not tell a
         correct pair from a mispriced one. One update call zeroes them all, so
         the window is a single request wide, and the snapshot above closes it. */
      if (plan.removes.length) {
        const updates = {};
        for (const key of plan.removes) updates[key] = 0;
        await postJSON(url('cart_update_url', '/cart/update'), { updates });
      }
      await postJSON(url('cart_add_url', '/cart/add'), { items: plan.adds });

      const after = await getCart();
      const problem = verify(after, plan.expect);
      if (problem) {
        console.warn('[lp-kaching-bundle] verification failed, restoring cart —', problem);
        const addedKeys = (after.items || [])
          .filter((line) => {
            const s = readStamp(line);
            return s && s.id === plan.expect.instanceId;
          })
          .map((line) => line.key);
        await restore(saved, addedKeys).catch((err) =>
          console.warn('[lp-kaching-bundle] restore failed', err)
        );
        return { ok: false, reason: 'verification-failed', problem };
      }

      return { ok: true, bar, cart: after };
    } catch (err) {
      console.warn('[lp-kaching-bundle] upgrade aborted', err);
      return { ok: false, reason: 'error', error: err };
    }
  };

  window.lpKachingBundle.upgrade = upgrade;
  window.lpKachingBundle.newInstanceId = newInstanceId;
})();
